import { SetStateAction, useEffect, useState } from 'react';
import {
  ColumnDefinition,
  RowData,
  TableActions,
} from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { useTableFeatures } from '@scope/scope-one-ui/core/DataTable/hooks/useTableFeatures';

type UseRsr = (props: UseRsrProps) => UseRsrReturn;

type UseRsrProps = {
  initialColumns: ColumnDefinition[];
  defaultSortField: string;
};
type UseRsrReturn = {
  tableActions: TableActions;
  selectToggle: (id) => void;
  selectedIds: string[];
  setSelectedIds: React.Dispatch<SetStateAction<string[]>>;
  searchedValue: string;
  sort: { field: string; order: 'ASC' | 'DESC' };
  loading: boolean;
  setLoading: React.Dispatch<SetStateAction<boolean>>;
  pagination: { page: number; limit: number };
  rows: RowData[];
  setRows: React.Dispatch<SetStateAction<RowData[]>>;
  total: number;
  setTotal: React.Dispatch<SetStateAction<number>>;
  lastPage: number;
  setLastPage: React.Dispatch<SetStateAction<number>>;
  selectAllToggle: () => void;
  isSelectedAll: boolean;
  clearSelectedIds: () => void;
};

export const useRSR: UseRsr = ({ initialColumns, defaultSortField }) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [searchedValue, setSearchedValue] = useState('');
  const [isSelectedAll, setIsSelectedAll] = useState(false);

  useEffect(() => {
    setLastPage(Math.ceil(total / pagination.limit));
  }, [total]);

  const clearSelectedIds = () => {
    setIsSelectedAll(false);
    setSelectedIds([]);
  };

  const selectAllToggle = () => {
    if (!isSelectedAll && selectedIds.length === 0) {
      setIsSelectedAll(true);
      setSelectedIds(rows.map((row) => row.id));
    } else {
      setIsSelectedAll(false);
      setSelectedIds([]);
    }
  };

  const [sort, setSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({
    field: defaultSortField,
    order: 'ASC',
  });

  const selectToggle = (id: string) => {
    const isSelected = selectedIds.includes(id);
    return isSelected
      ? setSelectedIds((prevState) => prevState.filter((ids) => ids !== id))
      : setSelectedIds([...selectedIds, id]);
  };
  const tableActions: TableActions = {
    ...useTableFeatures(rows, initialColumns),
    dataRows: rows,
    currentPage: pagination.page,
    rowsPerPage: pagination.limit,
    lastPage,
    updateSearching: (searched) => setSearchedValue(searched),
    searchedValue,
    updatePagination: (page, limit) => {
      const newLast = Math.ceil((total ?? 1) / limit);
      setLastPage(newLast);
      if (page > newLast) {
        setPagination({ page: newLast, limit });
      } else {
        setPagination({ page, limit });
      }
    },
    updateOrdering: (field, order) => {
      setSort({ field, order: order.toUpperCase() as 'ASC' | 'DESC' });
    },
    sort: { field: sort.field, order: sort.order.toLowerCase() as 'asc' | 'desc' },
  };

  return {
    tableActions,
    selectToggle,
    selectedIds,
    setSelectedIds,
    searchedValue,
    setSearchedValue,
    sort,
    setSort,
    loading,
    setLoading,
    pagination,
    rows,
    setRows,
    selectAllToggle,
    isSelectedAll,
    clearSelectedIds,
    total,
    setTotal,
    lastPage,
    setLastPage,
  };
};
